// #936 — menu lateral do painel. Regra PURA (camada 3a): quais itens cada papel
// vê e qual item a rota atual acende. O componente (`AppShell`) só desenha.
//
// **O menu não é o gate.** Esconder "Gestão" de quem não é verificado é
// arrumação de tela; quem barra `/business` digitado na URL continua sendo o
// `AuthGuard` da rota. Não use `navSectionsFor` pra decidir acesso.

export type NavItemId = 'events' | 'results' | 'users' | 'trackers' | 'business' | 'growth' | 'flyover';
export type NavSectionId = 'organization' | 'management';

export interface NavItem {
  id: NavItemId;
  to: string;
}

export interface NavSection {
  id: NavSectionId;
  items: readonly NavItem[];
}

/// Índice de resultados: escolhe o evento antes de abrir a lista. A página de
/// resultados é por evento (`/events/:id/results`), então o item do menu, sem
/// evento na mão, precisa de um lugar pra cair.
export const RESULTS_INDEX_PATH = '/events/results';

const ORGANIZATION: NavSection = {
  id: 'organization',
  items: [
    { id: 'events', to: '/events' },
    { id: 'results', to: RESULTS_INDEX_PATH },
  ],
};

const MANAGEMENT: NavSection = {
  id: 'management',
  items: [
    { id: 'users', to: '/users' },
    { id: 'trackers', to: '/trackers' },
    { id: 'business', to: '/business' },
    { id: 'growth', to: '/business/growth' },
    { id: 'flyover', to: '/flyover' },
  ],
};

export interface NavRoles {
  isOrganizer: boolean;
  isVerifiedOrganizer: boolean;
}

/// Seções do menu, espelhando o papel que o `AuthGuard` exige em cada rota:
/// Organização é de `organizer`, Gestão (usuários e painel de negócio) é do
/// super-admin `verified_organizer`. Os papéis são flat (`hasRole` é
/// `includes`), então um não implica o outro.
///
/// Sem nenhum dos dois (ex.: só `athlete`, que o `IndexRedirect` manda pra
/// `/events`), o menu fica **sem seções**: oferecer link que só leva a "Acesso
/// restrito" é ruído. O rodapé com o "Sair" continua, e é o que tira a pessoa
/// dali.
export function navSectionsFor({ isOrganizer, isVerifiedOrganizer }: NavRoles): readonly NavSection[] {
  const sections: NavSection[] = [];
  if (isOrganizer) sections.push(ORGANIZATION);
  if (isVerifiedOrganizer) sections.push(MANAGEMENT);
  return sections;
}

/// Item aceso pela rota. Não é prefixo ingênuo: `/events/:id/results` é
/// Resultados (não Eventos) e `/business/growth` é Crescimento (não Negócio).
/// Rota fora do menu (NotFound) não acende nada.
export function activeNavItem(pathname: string): NavItemId | null {
  const parts = pathname.split('/').filter(Boolean);
  const [root, second, third] = parts;
  if (root === 'events') {
    if (second === 'results' && parts.length === 2) return 'results';
    if (third === 'results' && parts.length === 3) return 'results';
    return 'events';
  }
  if (root === 'users') return 'users';
  if (root === 'trackers') return 'trackers';
  if (root === 'flyover') return 'flyover';
  if (root === 'business') return second === 'growth' ? 'growth' : 'business';
  return null;
}
